import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Input,
  Button,
  Typography,
  Row,
  Col,
  Space,
  message,
  QRCode,
  Switch,
  Form,
  Select,
  Tooltip,
} from "antd";
import {
  CopyOutlined,
  ExportOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import styles from "./style.module.scss";
import { BusinessUnit, URL_SOURCE_TYPE } from "src/utils/enums";
import { FULL_URL } from "src/utils/consts";
import { useAppDispatch, useAppSelector } from "src/hooks/useRedux";
import { setMockDataEnabled, setLocationRedirectEnabled, setSessionId } from "src/redux/slices/appSlice/AppSlice";
const { Title } = Typography;
enum InputNames {
  PERSONAL_ID = "personalId",
  BUSINESS_UNIT = "businessUnit",
  TRAVEL_POLICY_NUMBER = "travelPolicyNumber",
  CREATED_BY = "createdBy",
  CREATED_BY_SMS_SHARE = "createdBySmsShare",
  CREATED_BY_SMS = "createdBySms",
  CAMPAIGN_ID = "campaignID",
  CAMPAIGN_NAME = "campaignName",
  CAMPAIGN_SOURCE = "campaignSource",
  CAMPAIGN_MEDIUM = "campaignMedium",
  CAMPAIGN_TERM = "campaignTerm",
  CAMPAIGN_CONTENT = "campaignContent",
}

// Add enum for CreatedBy options
enum CreatedByOptions {
  DIGITAL_AGENT = "digital-agent",
  ENSURE = "ensure",
}
enum CreatedBySmsOptions {
  NONE = "",
  ENSURE = "ensure",
  CRM = "crm",
  BOT = "bot",
}

// Add enum for BusinessUnit options


// Add default business unit options for the dropdown
const DEFAULT_BUSINESS_UNIT_OPTIONS = [
  {
    value: BusinessUnit.PCIL,
    label: "PCIL",
  },
];

interface ChannelInput {
  id: number;
  name: InputNames;
  placeholder: string;
  isRequired?: boolean;
  isStatic?: boolean;
  defaultValue?: string;
}

interface Channel {
  id: number;
  name: string;
  url: string;
  inputs: ChannelInput[];
  isExternal?: boolean;
}

const TestingScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const { mockDataEnabled, locationRedirectEnabled } = useAppSelector((state) => state.app);

  const [showQrSection, setShowQrSection] = useState<number | null>(null);
  const [mockDataEnabledSwitch, setMockDataEnabledSwitch] =
    useState(mockDataEnabled);
  const [locationRedirectEnabledSwitch, setLocationRedirectEnabledSwitch] = useState(locationRedirectEnabled);

  const [personalId, setPersonalId] = useState("");
  const [businessUnit, setBusinessUnit] = useState(BusinessUnit.PCIL);
  const [travelPolicyNumber, setTravelPolicyNumber] = useState("");
  const [createdBy, setCreatedBy] = useState(CreatedByOptions.DIGITAL_AGENT);
  const [createdBySmsShare, setCreatedBySmsShare] = useState("");
  const [createdBySms, setCreatedBySms] = useState(CreatedBySmsOptions.NONE);

  // Add state for campaign tracking parameters
  const [campaignID, setCampaignID] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [campaignSource, setCampaignSource] = useState("");
  const [campaignMedium, setCampaignMedium] = useState("");
  const [campaignTerm, setCampaignTerm] = useState("");
  const [campaignContent, setCampaignContent] = useState("");
  const prodUrl = "https://pfs-onboarding.passportcard.co.il";

  // Update clearAllInputs function to include new fields
  const clearAllInputs = () => {
    setPersonalId("");
    setBusinessUnit(BusinessUnit.PCIL);
    setTravelPolicyNumber("");
    setCreatedBy(CreatedByOptions.DIGITAL_AGENT);
    setCreatedBySmsShare("");
    setCreatedBySms(CreatedBySmsOptions.NONE);
    setCampaignID("");
    setCampaignName("");
    setCampaignSource("");
    setCampaignMedium("");
    setCampaignTerm("");
    setCampaignContent("");
    // clean sessionId from store
    dispatch(setSessionId(""));
    message.success("All inputs cleared!");
  };

  useEffect(() => {
    if (mockDataEnabled) {
      setPersonalId("1234567890");
      setBusinessUnit(BusinessUnit.PCIL);
      setTravelPolicyNumber("1234567890");
    }
  }, [mockDataEnabled]);

  // Add campaign parameters to URL
  const getCampaignParams = () => {
    let params = "";
    if (campaignID) params += `&utm_id=${campaignID}`;
    if (campaignName) params += `&utm_campaign=${campaignName}`;
    if (campaignSource) params += `&utm_source=${campaignSource}`;
    if (campaignMedium) params += `&utm_medium=${campaignMedium}`;
    if (campaignTerm) params += `&utm_term=${campaignTerm}`;
    if (campaignContent) params += `&utm_content=${campaignContent}`;
    return params;
  };
  const getEnsureParams = () => {
    let params = "";
    if (personalId) params += `&personalId=${personalId}`;
    if (businessUnit) params += `&businessUnit=${businessUnit}`;
    if (travelPolicyNumber)
      params += `&travelPolicyNumber=${travelPolicyNumber}`;
    return params;
  };
  const getPfsRepParams = () => {
    let params = "";
    if (personalId) params += `&personalId=${personalId}`;
    if (businessUnit) params += `&businessUnit=${businessUnit}`;
    if (createdBy) params += `&createdBy=${createdBy}`;
    if (travelPolicyNumber)
      params += `&travelPolicyNumber=${travelPolicyNumber}`;
    return params;
  };
  const getSmsParams = () => {
    let params = "";
    if (businessUnit) params += `&businessUnit=${businessUnit}`;
    if (createdBySms) params += `&createdBy=${createdBySms}`;
    return params;
  };

  const availableChannels: Channel[] = useMemo(
    () => [
      {
        id: 1,
        name: URL_SOURCE_TYPE.ENSURE,
        url: `/${URL_SOURCE_TYPE.ENSURE}/agent?${getEnsureParams()}`,
        isExternal: false,
        inputs: [
          {
            id: 1,
            name: InputNames.PERSONAL_ID,
            placeholder: "Encrypted Personal ID",
            isRequired: true,
          },
          {
            id: 2,
            name: InputNames.BUSINESS_UNIT,
            placeholder: "Bussiness Unit",
            isRequired: true,
          },
          {
            id: 3,
            name: InputNames.TRAVEL_POLICY_NUMBER,
            placeholder: "Travel Policy Number",
            isRequired: false,
          },
        ],
      },
      {
        id: 2,
        name: URL_SOURCE_TYPE.PFS_REP,
        url: `/${URL_SOURCE_TYPE.PFS_REP}/agent?${getPfsRepParams()}`,
        isExternal: false,
        inputs: [
          {
            id: 1,
            name: InputNames.PERSONAL_ID,
            placeholder: "Personal ID",
            isRequired: true,
          },
          {
            id: 2,
            name: InputNames.BUSINESS_UNIT,
            placeholder: "Bussiness Unit",
            isRequired: true,
          },
          {
            id: 3,
            name: InputNames.CREATED_BY,
            placeholder: "Created By",
            isRequired: true,
          },
          {
            id: 4,
            name: InputNames.TRAVEL_POLICY_NUMBER,
            placeholder: "Travel Policy Number",
            isRequired: true,
          },
        ],
      },
      {
        id: 3,
        name: URL_SOURCE_TYPE.REP_MANUAL,
        url: `/${URL_SOURCE_TYPE.REP_MANUAL}/login/details`,
        isExternal: false,
        inputs: [],
      },
      {
        id: 4,
        name: URL_SOURCE_TYPE.SMS,
        // url: `/${URL_SOURCE_TYPE.SMS}/login/details?${getSmsParams()}`,
        url: `/${URL_SOURCE_TYPE.SMS}/pc-pay?${getSmsParams()}`,
        isExternal: false,
        inputs: [
          {
            id: 1,
            name: InputNames.BUSINESS_UNIT,
            placeholder: "Bussiness Unit",
            isRequired: true,
          },
          {
            id: 2,
            name: InputNames.CREATED_BY_SMS,
            placeholder: "Created By",
            // isStatic: false,
            // defaultValue: "ensure",
          },
        ],
      },
      {
        id: 5,
        name: URL_SOURCE_TYPE.MARKETING,
        url: `/${URL_SOURCE_TYPE.MARKETING}/pc-pay`,
        isExternal: false,
        inputs: [],
      },
      {
        id: 6,
        name: URL_SOURCE_TYPE.CAMPAIGN,
        url: `/${URL_SOURCE_TYPE.CAMPAIGN}/pc-pay?${getCampaignParams()}`,
        isExternal: false,
        inputs: [
          // Add campaign tracking inputs
          {
            id: 1,
            name: InputNames.CAMPAIGN_ID,
            placeholder: "Campaign ID (utm_id)",
            isRequired: false,
          },
          {
            id: 2,
            name: InputNames.CAMPAIGN_NAME,
            placeholder: "Campaign Name (utm_campaign)",
            isRequired: false,
          },
          {
            id: 3,
            name: InputNames.CAMPAIGN_SOURCE,
            placeholder: "Campaign Source (utm_source)",
            isRequired: false,
          },
          {
            id: 4,
            name: InputNames.CAMPAIGN_MEDIUM,
            placeholder: "Campaign Medium (utm_medium)",
            isRequired: false,
          },
          {
            id: 5,
            name: InputNames.CAMPAIGN_TERM,
            placeholder: "Campaign Term (utm_term)",
            isRequired: false,
          },
          {
            id: 6,
            name: InputNames.CAMPAIGN_CONTENT,
            placeholder: "Campaign Content (utm_content)",
            isRequired: false,
          },
        ],
      },

      {
        id: 7,
        name: URL_SOURCE_TYPE.SMS_SHARE,
        // url: `/${URL_SOURCE_TYPE.SMS_SHARE
        //   }/login/details?businessUnit=${businessUnit}&createdBy=${createdBySmsShare}${getCampaignParams()}`,
        url: `/${URL_SOURCE_TYPE.SMS_SHARE}/pc-pay?businessUnit=${businessUnit}&createdBy=${createdBySmsShare}`,

        isExternal: false,
        inputs: [
          {
            id: 1,
            name: InputNames.BUSINESS_UNIT,
            placeholder: "Bussiness Unit",
            isRequired: true,
            isStatic: true,
            defaultValue: "PCIL",
          },
          {
            id: 2,
            name: InputNames.CREATED_BY_SMS_SHARE,
            placeholder: "Created By",
            isRequired: true,
            isStatic: true,
            defaultValue: "digital-agent",
          },
        ],
      },
      {
        id: 8,
        name: URL_SOURCE_TYPE.DIGITAL,
        url: `https://pc-qa-salesprocess-v2.passportcard.com/existing`,
        isExternal: true,
        inputs: [],
      },
      {
        id: 9,
        name: URL_SOURCE_TYPE.DIGITAL_AGENT,
        url: `https://pc-qa-salesprocess-v2.passportcard.com/existing?agentSaleNumberID=9GPfkGCQ0wTFePpdrU4xbg%3D%3D`,
        isExternal: true,
        inputs: [],
      },
      {
        id: 10,
        name: URL_SOURCE_TYPE.APP,
        url: `/${URL_SOURCE_TYPE.APP}/pc-pay`,
        isExternal: false,
        inputs: [],
      },
    ],
    [
      personalId,
      businessUnit,
      travelPolicyNumber,
      createdBy,
      createdBySmsShare,
      createdBySms,
      campaignID,
      campaignName,
      campaignSource,
      campaignMedium,
      campaignTerm,
      campaignContent,
    ]
  );

  // Initialize state with defaultValues from inputs
  useEffect(() => {
    // Collect all inputs from all channels
    const allInputs = availableChannels.flatMap((channel) => channel.inputs);

    // Set state variables based on defaultValues if they exist
    allInputs.forEach((input) => {
      if (input.defaultValue) {
        switch (input.name) {
          case InputNames.PERSONAL_ID:
            setPersonalId(input.defaultValue);
            break;
          case InputNames.BUSINESS_UNIT:
            setBusinessUnit(input.defaultValue as BusinessUnit);
            break;
          case InputNames.TRAVEL_POLICY_NUMBER:
            setTravelPolicyNumber(input.defaultValue);
            break;
          case InputNames.CREATED_BY:
            setCreatedBy(input.defaultValue as CreatedByOptions);
            break;
          case InputNames.CREATED_BY_SMS_SHARE:
            setCreatedBySmsShare(input.defaultValue);
            break;
          case InputNames.CREATED_BY_SMS:
            setCreatedBySms(input.defaultValue as CreatedBySmsOptions);
            break;
        }
      }
    });
  }, []); // Run only once on component mount

  const validateInputs = (channel: Channel): boolean => {
    // If mockDataEnabled is true, skip validation
    if (mockDataEnabled) {
      return true;
    }

    // If there are no inputs, validation always passes
    if (channel.inputs.length === 0) {
      return true;
    }
    const missingRequiredInputs = channel.inputs
      .filter((input) => input.isRequired)
      .some((input) => {
        switch (input.name) {
          case InputNames.PERSONAL_ID:
            return !personalId;
          case InputNames.BUSINESS_UNIT:
            return !businessUnit;
          case InputNames.TRAVEL_POLICY_NUMBER:
            return !travelPolicyNumber;
          case InputNames.CREATED_BY:
            return !createdBy;
          default:
            return false;
        }
      });

    if (missingRequiredInputs) {
      message.error("Please fill all required fields");
      return false;
    }

    return true;
  };

  const openOnWeb = (url: string) => {
    dispatch(setSessionId(""));
    // Find the current channel
    const currentChannel = availableChannels.find(
      (channel) => channel.url === url
    );
    if (!currentChannel) return;

    // Only proceed if validation passes
    if (!validateInputs(currentChannel)) {
      return;
    }

    window.open(url, "_blank");
  };

  const openOnMobile = (channel: Channel) => {
    // Only proceed if validation passes
    if (!validateInputs(channel)) {
      return;
    }

    // Toggle QR code for this specific channel
    setShowQrSection((prevState) =>
      prevState === channel.id ? null : channel.id
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <Title level={2} className={styles.title}>
          Welcome to onBoarding Testing Screen
        </Title>
        <Title level={4} className={styles.subtitle}>
          Please choose channel to continue
        </Title>
        <div className={styles.mockDataSwitch}>
          <Space align="center">
            <Switch
              checked={mockDataEnabledSwitch}
              onChange={(checked) => {
                setMockDataEnabledSwitch(checked);
                dispatch(setMockDataEnabled(checked));
                // when mock data disabled, clear all inputs
                if (!checked) {
                  clearAllInputs();
                }
              }}
            />
            <Typography.Text>Mock Data</Typography.Text>
            <Switch
              checked={locationRedirectEnabledSwitch}
              onChange={(checked) => {
                setLocationRedirectEnabledSwitch(checked);
                dispatch(setLocationRedirectEnabled(checked));
              }}
            />
            <Typography.Text>Location Redirect</Typography.Text>
            <Button type="default" onClick={clearAllInputs}>
              Clean
            </Button>
          </Space>
        </div>
      </div>

      <Row gutter={[32, 32]} justify="center" className={styles.cardsContainer}>
        {availableChannels.map((channel) => (
          <Col xs={24} sm={20} md={12} lg={8} key={channel.id}>
            <Card title={channel.name} bordered className={styles.channelCard}>
              <div className={styles.cardContent}>
                <div className={styles.inputsContainer}>
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size="middle"
                  >
                    {!mockDataEnabled && channel.inputs.length > 0 ? (
                      channel.inputs.map((input) => (
                        <Form.Item
                          key={input.id}
                          label={input.placeholder}
                          required={input.isRequired}
                          className={styles.formItem}
                          labelCol={{ span: 24 }}
                          wrapperCol={{ span: 24 }}
                          tooltip={
                            input.isRequired
                              ? "This field is required"
                              : undefined
                          }
                        >
                          {input.name === InputNames.CREATED_BY ? (
                            <Select
                              placeholder={input.placeholder}
                              size="large"
                              value={createdBy}
                              disabled={input.isStatic}
                              className={styles.input}
                              onChange={(value) =>
                                setCreatedBy(value as CreatedByOptions)
                              }
                              status={
                                input.isRequired && !createdBy
                                  ? "error"
                                  : undefined
                              }
                              options={
                                // Use CreatedBySmsOptions only for SMS channel
                                channel.name === URL_SOURCE_TYPE.SMS
                                  ? [
                                    {
                                      value: CreatedBySmsOptions.NONE,
                                      label: "none",
                                    },
                                    {
                                      value: CreatedBySmsOptions.ENSURE,
                                      label: "ensure",
                                    },
                                    {
                                      value: CreatedBySmsOptions.CRM,
                                      label: "crm",
                                    },
                                    {
                                      value: CreatedBySmsOptions.BOT,
                                      label: "bot",
                                    },
                                  ]
                                  : [
                                    {
                                      value: CreatedByOptions.DIGITAL_AGENT,
                                      label: "digital-agent",
                                    },
                                    {
                                      value: CreatedByOptions.ENSURE,
                                      label: "ensure",
                                    },
                                  ]
                              }
                            />
                          ) : input.name === InputNames.BUSINESS_UNIT ? (
                            <Select
                              placeholder={input.placeholder}
                              size="large"
                              value={businessUnit}
                              disabled={input.isStatic}
                              className={styles.input}
                              onChange={(value) => setBusinessUnit(value)}
                              status={
                                input.isRequired && !businessUnit
                                  ? "error"
                                  : undefined
                              }
                              // mode="tags"
                              options={DEFAULT_BUSINESS_UNIT_OPTIONS}
                            // tokenSeparators={[","]}
                            // allowClear
                            />
                          ) : input.name === InputNames.CREATED_BY_SMS && channel.name === URL_SOURCE_TYPE.SMS ? (
                            <Select
                              placeholder={input.placeholder}
                              size="large"
                              value={createdBySms}
                              disabled={input.isStatic}
                              className={styles.input}
                              onChange={(value) => setCreatedBySms(value as CreatedBySmsOptions)}
                              status={input.isRequired && !createdBySms ? "error" : undefined}
                              options={[
                                {
                                  value: CreatedBySmsOptions.NONE,
                                  label: "none",
                                },
                                {
                                  value: CreatedBySmsOptions.ENSURE,
                                  label: "ensure",
                                },
                                {
                                  value: CreatedBySmsOptions.CRM,
                                  label: "crm",
                                },
                                {
                                  value: CreatedBySmsOptions.BOT,
                                  label: "bot",
                                },
                              ]}
                            />
                          ) : (
                            <Input
                              placeholder={input.placeholder}
                              size="large"
                              allowClear
                              value={
                                input.name === InputNames.PERSONAL_ID
                                  ? personalId
                                  : input.name ===
                                    InputNames.TRAVEL_POLICY_NUMBER
                                    ? travelPolicyNumber
                                    : input.name === InputNames.CAMPAIGN_ID
                                      ? campaignID
                                      : input.name === InputNames.CAMPAIGN_NAME
                                        ? campaignName
                                        : input.name === InputNames.CAMPAIGN_SOURCE
                                          ? campaignSource
                                          : input.name === InputNames.CAMPAIGN_MEDIUM
                                            ? campaignMedium
                                            : input.name === InputNames.CAMPAIGN_TERM
                                              ? campaignTerm
                                              : input.name === InputNames.CAMPAIGN_CONTENT
                                                ? campaignContent
                                                : input.defaultValue
                              }
                              disabled={input.isStatic}
                              className={styles.input}
                              onChange={(e) => {
                                if (input.name === InputNames.PERSONAL_ID) {
                                  setPersonalId(e.target.value);
                                } else if (
                                  input.name === InputNames.TRAVEL_POLICY_NUMBER
                                ) {
                                  setTravelPolicyNumber(e.target.value);
                                } else if (
                                  input.name === InputNames.CAMPAIGN_ID
                                ) {
                                  setCampaignID(e.target.value);
                                } else if (
                                  input.name === InputNames.CAMPAIGN_NAME
                                ) {
                                  setCampaignName(e.target.value);
                                } else if (
                                  input.name === InputNames.CAMPAIGN_SOURCE
                                ) {
                                  setCampaignSource(e.target.value);
                                } else if (
                                  input.name === InputNames.CAMPAIGN_MEDIUM
                                ) {
                                  setCampaignMedium(e.target.value);
                                } else if (
                                  input.name === InputNames.CAMPAIGN_TERM
                                ) {
                                  setCampaignTerm(e.target.value);
                                } else if (
                                  input.name === InputNames.CAMPAIGN_CONTENT
                                ) {
                                  setCampaignContent(e.target.value);
                                }
                              }}
                              status={
                                input.isRequired &&
                                  ((input.name === InputNames.PERSONAL_ID &&
                                    !personalId) ||
                                    (input.name ===
                                      InputNames.TRAVEL_POLICY_NUMBER &&
                                      !travelPolicyNumber))
                                  ? "error"
                                  : undefined
                              }
                            />
                          )}
                        </Form.Item>
                      ))
                    ) : (
                      <div className={styles.noInputs}>
                        <Typography.Text type="secondary">
                          <InfoCircleOutlined style={{ marginRight: 8 }} />
                          {mockDataEnabled
                            ? "Using Mock Data"
                            : "No Inputs Needed"}
                        </Typography.Text>
                      </div>
                    )}
                  </Space>

                  {showQrSection === channel.id && (
                    <div className={styles.qrSection}>
                      <Space direction="vertical" align="center">
                        <QRCode
                          value={
                            channel.isExternal
                              ? channel.url
                              : `${FULL_URL}${channel.url}`
                          }
                          size={100}
                        />
                      </Space>
                    </div>
                  )}
                </div>

                <div className={styles.buttonGroup}>
                  <Button
                    type="primary"
                    size="large"
                    color="default"
                    variant="solid"
                    style={{ width: "100%" }}
                    onClick={() => openOnWeb(channel.url)}
                    icon={<ExportOutlined />}
                  >
                    Web
                  </Button>
                  <Tooltip
                    title={
                      mockDataEnabled
                        ? "To view on mobile please disable mock data"
                        : ""
                    }
                    open={mockDataEnabled ? undefined : false}
                  >
                    <Button
                      size="large"
                      style={{ width: "100%" }}
                      disabled={mockDataEnabled}
                      onClick={() => openOnMobile(channel)}
                    >
                      Mobile
                    </Button>
                  </Tooltip>
                  <Space>
                    <Button
                      size="large"
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(
                          channel.isExternal
                            ? channel.url
                            : `${FULL_URL}${channel.url}`
                        );
                        message.success("URL copied to clipboard!");
                      }}
                    />
                  </Space>

                  <Button
                    size="large"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      navigator.clipboard.writeText(
                        channel.isExternal
                          ? channel.url
                          : `${prodUrl}${channel.url}`
                      );
                      message.success("URL copied to clipboard!");
                    }}
                  >
                    Copy Prod URL
                  </Button>


                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default TestingScreen;
